import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Paper, Grid, Switch,
  FormControlLabel, Alert, CircularProgress, MenuItem, InputAdornment,
  IconButton, Divider, Card, CardContent
} from '@mui/material';
import {
  Save as SaveIcon, Cancel as CancelIcon, Add as AddIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import { api } from '../../services/api';
import { enrollmentDeadlineInputValue, enrollmentDeadlineToISOString } from '../../utils/dateUtils';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' }, { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' }, { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio Buttons' }, { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' }, { value: 'phone', label: 'Phone' }
];

const emptyForm = {
  name: '', location: '', description: '', rules: '',
  term: '', curriculum: '',
  male_capacity: '', female_capacity: '', min_grade: '', max_grade: '',
  price: '', volume_discount: '', minimum_price: '',
  enrollment_deadline: '', enrollment_open: true,
  public: true,
  enrollee_role: 'child', enroller_role: 'parent', admin_role: 'admin',
  enrollment_completion_role: '',
  // feature flags
  feature_term: false, feature_per_role_pricing: false,
  feature_volume_discount: false, feature_custom_fields: false, feature_markdown: false
};

function ProgramForm({ isEmbedded = false, programId: propProgramId, onSave }) {
  const { id: routeId } = useParams();
  const id = propProgramId || routeId;
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState(emptyForm);
  const [priceRoles, setPriceRoles] = useState([{ role: 'default', price: '', isDefault: true }]);
  const [customFields, setCustomFields] = useState([]);
  const [optionsInputs, setOptionsInputs] = useState({});
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [ve, setVe] = useState({});

  useEffect(() => { if (isEditMode) fetchProgram(); }, [id]);

  const fetchProgram = async () => {
    try {
      setIsLoading(true);
      const p = await api.getProgram(id);
      setFormData({
        name: p.name || '', location: p.location || '', description: p.description || '', rules: p.rules || '',
        term: p.term || '', curriculum: p.curriculum || '',
        male_capacity: p.male_capacity ?? '', female_capacity: p.female_capacity ?? '',
        min_grade: p.min_grade ?? '', max_grade: p.max_grade ?? '',
        price: p.price ?? '', volume_discount: p.volume_discount ?? '', minimum_price: p.minimum_price ?? '',
        enrollment_deadline: enrollmentDeadlineInputValue(p.enrollment_deadline),
        enrollment_open: p.enrollment_open ?? true,
        public: p.public ?? true,
        enrollee_role: p.enrollee_role || 'child', enroller_role: p.enroller_role || 'parent', admin_role: p.admin_role || 'admin',
        enrollment_completion_role: p.enrollment_completion_role || '',
        feature_term: !!p.feature_term,
        feature_per_role_pricing: !!p.feature_per_role_pricing, feature_volume_discount: !!p.feature_volume_discount,
        feature_custom_fields: !!p.feature_custom_fields, feature_markdown: !!p.feature_markdown
      });
      setCustomFields(p.custom_fields || []);
      const oi = {};
      (p.custom_fields || []).forEach((f, i) => { if (f.options?.length) oi[i] = f.options.join(', '); });
      setOptionsInputs(oi);
      const map = p.price_map || {};
      const roles = Object.entries(map).map(([role, price]) => ({ role, price: String(price), isDefault: role === 'default' }));
      if (!roles.some((r) => r.isDefault)) roles.unshift({ role: 'default', price: p.price ? String(p.price) : '', isDefault: true });
      setPriceRoles(roles.length ? roles : [{ role: 'default', price: '', isDefault: true }]);
    } catch (err) {
      console.error('Error fetching program:', err);
      setError(err.response?.data?.error || 'Failed to load program');
    } finally { setIsLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (ve[name]) setVe((prev) => ({ ...prev, [name]: '' }));
  };

  // price roles
  const setPriceRole = (i, field, value) => { const n = [...priceRoles]; n[i][field] = value; setPriceRoles(n); };
  const addPriceRole = () => setPriceRoles([...priceRoles, { role: '', price: '', isDefault: false }]);
  const removePriceRole = (i) => { if (!priceRoles[i].isDefault) setPriceRoles(priceRoles.filter((_, idx) => idx !== i)); };

  // custom fields
  const setField = (i, field, value) => { const n = [...customFields]; n[i][field] = value; setCustomFields(n); };
  const setFieldOptions = (i, value) => {
    setOptionsInputs((prev) => ({ ...prev, [i]: value }));
    setField(i, 'options', value.split(',').map((o) => o.trim()).filter(Boolean));
  };
  const addField = () => setCustomFields([...customFields, { id: `field_${Date.now()}`, label: '', type: 'text', required: false, options: [], placeholder: '' }]);
  const removeField = (i) => { setCustomFields(customFields.filter((_, idx) => idx !== i)); setOptionsInputs((prev) => { const n = { ...prev }; delete n[i]; return n; }); };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Program name is required';
    if (formData.feature_term && !formData.term.trim()) e.term = 'Term is required';
    if (formData.male_capacity === '' || formData.male_capacity < 0) e.male_capacity = 'Valid capacity is required';
    if (formData.female_capacity === '' || formData.female_capacity < 0) e.female_capacity = 'Valid capacity is required';
    if (formData.min_grade !== '' && formData.max_grade !== '' && parseInt(formData.min_grade) > parseInt(formData.max_grade)) e.min_grade = 'Min grade cannot be greater than max grade';
    if (!formData.feature_per_role_pricing && (formData.price === '' || formData.price < 0)) e.price = 'Valid price is required';
    if (formData.enrollment_completion_role && !/^[a-z][a-z0-9_]{1,99}$/.test(formData.enrollment_completion_role)) {
      e.enrollment_completion_role = 'Use 2–100 lowercase letters, numbers, or underscores; start with a letter';
    }
    setVe(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true); setError('');
    try {
      // Pricing
      let priceMap = {};
      let basePrice = 0;
      if (formData.feature_per_role_pricing) {
        priceRoles.forEach((pr) => { if (pr.role && pr.price !== '') priceMap[pr.role] = parseFloat(pr.price); });
        if (priceMap['default'] === undefined) { setError('Default price is required'); setIsSaving(false); return; }
        basePrice = priceMap['default'];
      } else {
        basePrice = parseFloat(formData.price);
        priceMap = { default: basePrice };
      }

      const data = {
        name: formData.name, location: formData.location, description: formData.description, rules: formData.rules,
        term: formData.feature_term ? formData.term : '',
        curriculum: formData.feature_term ? formData.curriculum : '',
        male_capacity: parseInt(formData.male_capacity), female_capacity: parseInt(formData.female_capacity),
        min_grade: formData.min_grade !== '' ? parseInt(formData.min_grade) : null,
        max_grade: formData.max_grade !== '' ? parseInt(formData.max_grade) : null,
        price: basePrice, price_map: priceMap,
        volume_discount: formData.feature_volume_discount && formData.volume_discount !== '' ? parseFloat(formData.volume_discount) : null,
        minimum_price: formData.feature_volume_discount && formData.minimum_price !== '' ? parseFloat(formData.minimum_price) : null,
        enrollment_deadline: enrollmentDeadlineToISOString(formData.enrollment_deadline),
        enrollment_open: formData.enrollment_open,
        custom_fields: formData.feature_custom_fields ? customFields : [],
        public: formData.public,
        enrollee_role: formData.enrollee_role, enroller_role: formData.enroller_role, admin_role: formData.admin_role,
        enrollment_completion_role: formData.enrollment_completion_role.trim(),
        feature_term: formData.feature_term,
        feature_per_role_pricing: formData.feature_per_role_pricing, feature_volume_discount: formData.feature_volume_discount,
        feature_custom_fields: formData.feature_custom_fields, feature_markdown: formData.feature_markdown
      };

      if (isEditMode) await api.updateProgram(id, data); else await api.createProgram(data);
      if (isEmbedded && onSave) onSave(); else navigate('/admin/programs');
    } catch (err) {
      console.error('Error saving program:', err);
      setError(err.response?.data?.error || 'Failed to save program');
    } finally { setIsSaving(false); }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  const toggle = (name, label, hint) => (
    <Grid item xs={12} sm={6} md={4}>
      <FormControlLabel control={<Switch checked={formData[name]} onChange={handleChange} name={name} />} label={label} />
      {hint && <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>{hint}</Typography>}
    </Grid>
  );

  const gradeSelect = (name, label, err) => (
    <TextField fullWidth select label={label} name={name} value={formData[name]} onChange={handleChange}
      error={!!err} helperText={err} sx={{ minWidth: 200 }}>
      <MenuItem value="">None</MenuItem>
      {[...Array(13)].map((_, i) => <MenuItem key={i} value={i}>{i === 0 ? 'Kindergarten' : `Grade ${i}`}</MenuItem>)}
    </TextField>
  );

  const formContent = (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit}>
        {/* Feature toggles */}
        <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>Features</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Toggle the capabilities this program needs.</Typography>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {toggle('feature_term', 'Term & curriculum')}
          {toggle('feature_per_role_pricing', 'Per-role pricing')}
          {toggle('feature_volume_discount', 'Volume discount')}
          {toggle('feature_custom_fields', 'Custom signup fields')}
          {toggle('feature_markdown', 'Markdown description')}
        </Grid>
        <Divider sx={{ my: 3 }} />

        {/* Basic */}
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Basic Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={formData.feature_term ? 8 : 12}>
            <TextField fullWidth label="Program Name" name="name" value={formData.name} onChange={handleChange}
              error={!!ve.name} helperText={ve.name} required />
          </Grid>
          {formData.feature_term && (
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Term" name="term" value={formData.term} onChange={handleChange}
                error={!!ve.term} helperText={ve.term} placeholder="Fall 2024" required />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField fullWidth label="Location" name="location" value={formData.location} onChange={handleChange} placeholder="Main Campus, Building A" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label="Enrollment Deadline" name="enrollment_deadline" value={formData.enrollment_deadline}
              onChange={handleChange} InputLabelProps={{ shrink: true }} helperText="Optional – last day to enroll" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel control={<Switch checked={formData.enrollment_open} onChange={handleChange} name="enrollment_open" />} label="Enrollment Open" />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>Manually close enrollment early, regardless of the deadline.</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Program dates are derived from the program's sessions (managed after saving).</Typography>
          </Grid>
        </Grid>

        {/* Description / curriculum / rules */}
        <Box sx={{ mt: 3 }}>
          <TextField fullWidth multiline rows={3} label="Description" name="description" value={formData.description} onChange={handleChange} sx={{ mb: 2 }} />
          {formData.feature_markdown && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>Description will be rendered as Markdown.</Typography>
          )}
          {formData.feature_term && (
            <TextField fullWidth multiline rows={4} label="Curriculum" name="curriculum" value={formData.curriculum} onChange={handleChange} placeholder="What will students learn?" sx={{ mb: 2 }} />
          )}
          <TextField fullWidth multiline rows={6} label="Rules" name="rules" value={formData.rules} onChange={handleChange} placeholder="Program rules and expectations" />
        </Box>

        {/* Capacity */}
        <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>Capacity</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label="Male Capacity" name="male_capacity" value={formData.male_capacity} onChange={handleChange}
              error={!!ve.male_capacity} helperText={ve.male_capacity} inputProps={{ min: 0 }} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label="Female Capacity" name="female_capacity" value={formData.female_capacity} onChange={handleChange}
              error={!!ve.female_capacity} helperText={ve.female_capacity} inputProps={{ min: 0 }} required />
          </Grid>
        </Grid>

        {/* Grade */}
        <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>Grade Requirements (Optional)</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>{gradeSelect('min_grade', 'Minimum Grade', ve.min_grade)}</Grid>
          <Grid item xs={12} md={6}>{gradeSelect('max_grade', 'Maximum Grade')}</Grid>
        </Grid>

        {/* Pricing */}
        <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>Pricing</Typography>
        {formData.feature_per_role_pricing ? (
          <Box sx={{ mb: 2 }}>
            {priceRoles.map((pr, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                <TextField label="Role" value={pr.role} onChange={(e) => setPriceRole(i, 'role', e.target.value)}
                  placeholder="mentor, enrolled, new" sx={{ flex: 1 }} disabled={pr.isDefault}
                  helperText={pr.isDefault ? 'Default price (required)' : ''} />
                <TextField label="Price" type="number" value={pr.price} onChange={(e) => setPriceRole(i, 'price', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }} sx={{ flex: 1 }} helperText=" " />
                <IconButton color="error" onClick={() => removePriceRole(i)} disabled={pr.isDefault}
                  sx={{ visibility: pr.isDefault ? 'hidden' : 'visible', mt: 1 }}><DeleteIcon /></IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addPriceRole} variant="outlined" size="small">Add Price Tier</Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={formData.feature_volume_discount ? 4 : 12}>
              <TextField fullWidth type="number" label="Price" name="price" value={formData.price} onChange={handleChange}
                error={!!ve.price} helperText={ve.price} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01 }} required />
            </Grid>
            {formData.feature_volume_discount && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Volume Discount" name="volume_discount" value={formData.volume_discount} onChange={handleChange}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} inputProps={{ min: 0, step: 0.01 }} helperText="Per additional enrollment" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth type="number" label="Minimum Price" name="minimum_price" value={formData.minimum_price} onChange={handleChange}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} inputProps={{ min: 0, step: 0.01 }} helperText="After discounts" />
                </Grid>
              </>
            )}
          </Grid>
        )}

        {/* Custom fields */}
        {formData.feature_custom_fields && (
          <>
            <Divider sx={{ my: 4 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Custom Signup Fields</Typography>
              <Button startIcon={<AddIcon />} onClick={addField} variant="outlined" size="small">Add Field</Button>
            </Box>
            {customFields.map((field, i) => (
              <Card key={field.id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Field {i + 1}</Typography>
                    <IconButton size="small" color="error" onClick={() => removeField(i)}><DeleteIcon /></IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Field Label" value={field.label} onChange={(e) => setField(i, 'label', e.target.value)} placeholder="e.g., T-Shirt Size" size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth select label="Field Type" value={field.type} onChange={(e) => setField(i, 'type', e.target.value)} size="small">
                        {FIELD_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField fullWidth label="Placeholder" value={field.placeholder} onChange={(e) => setField(i, 'placeholder', e.target.value)} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel control={<Switch checked={field.required} onChange={(e) => setField(i, 'required', e.target.checked)} size="small" />} label="Required" />
                    </Grid>
                    {['select', 'radio', 'checkbox'].includes(field.type) && (
                      <Grid item xs={12}>
                        <TextField fullWidth label="Options (comma-separated)" value={optionsInputs[i] || ''} onChange={(e) => setFieldOptions(i, e.target.value)}
                          placeholder="Small, Medium, Large, XL" size="small" helperText="Enter options separated by commas" />
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            ))}
            {customFields.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No custom fields added. Click "Add Field" to create signup form fields.</Alert>}
          </>
        )}

        {/* Settings */}
        <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>Settings</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Role Granted After Completed Enrollment"
              name="enrollment_completion_role" value={formData.enrollment_completion_role} onChange={handleChange}
              placeholder="mvyp_fall_2026_enrolled" error={!!ve.enrollment_completion_role}
              helperText={ve.enrollment_completion_role || 'Optional. Granted by the backend only after successful payment; can be used for future per-role pricing. Use lowercase letters, numbers, and underscores.'} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={formData.public} onChange={handleChange} name="public" />} label="Public (visible to all users)" />
          </Grid>
        </Grid>

        {/* Actions */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={isSaving}>
            {isSaving ? 'Saving...' : (isEditMode ? 'Update Program' : 'Create Program')}
          </Button>
          {!isEmbedded && (
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => navigate('/admin/programs')} disabled={isSaving}>Cancel</Button>
          )}
        </Box>
      </form>
    </>
  );

  if (isEmbedded) return formContent;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="md">
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          {isEditMode ? 'Edit Program' : 'Create New Program'}
        </Typography>
        <Paper sx={{ p: 3, mt: 3 }}>{formContent}</Paper>
      </Container>
    </Box>
  );
}

export default ProgramForm;
